import { useGetScrapTypes } from '@/shared/hooks/api/preconsumerScrapMaterials'
import useI18n from '@/shared/hooks/useI18n'
import { CheckboxGroup, GenericCombobox, InputGroup } from '@/common'
import { useMemo, useState } from 'react'

export default function ScrapTypeSelect({
  installationId,
  scrapType,
  newScrapType,
  onScrapTypeChange,
  onNewScrapTypeChange
}: {
  installationId: string
  scrapType: string | null
  newScrapType: string | null
  onScrapTypeChange: (scrapType: string | null) => void
  onNewScrapTypeChange: (newScrapType: string | null) => void
}) {
  const { t } = useI18n()
  const { data: scrapTypes } = useGetScrapTypes(installationId)
  const [isScrapTypeNotListed, setIsScrapTypeNotListed] = useState(false)

  const onIsScrapTypeNotListedChange = (value: boolean) => {
    onScrapTypeChange(null)
    onNewScrapTypeChange(null)
    setIsScrapTypeNotListed(value)
  }

  const scrapTypeOptions = useMemo(() => {
    const generalScrapType = t('report:materials.preconsumerScrap.dialog.generalScrapType')
    const scrapTypeOptions =
      scrapTypes?.map(scrapType => ({ value: scrapType, label: scrapType })) ?? []

    if (scrapTypes?.includes(generalScrapType)) {
      return scrapTypeOptions.sort((prev, next) => prev.label.localeCompare(next.label))
    }

    return [...scrapTypeOptions, { value: generalScrapType, label: generalScrapType }].sort(
      (prev, next) => prev.label.localeCompare(next.label)
    )
  }, [scrapTypes])

  return (
    <div className="space-y-2">
      <GenericCombobox
        value={scrapType ? { value: scrapType, label: scrapType } : null}
        disabled={isScrapTypeNotListed}
        items={scrapTypeOptions}
        onChange={scrapType => onScrapTypeChange(scrapType?.value ?? '')}
        label={t('report:materials.preconsumerScrap.dialog.fields.typeOfScrap.label')}
      />
      <CheckboxGroup
        id="addScrapType"
        label={t('report:materials.preconsumerScrap.dialog.fields.typeOfScrapNotListed.label')}
        checked={isScrapTypeNotListed}
        onChange={onIsScrapTypeNotListedChange}
      />
      <InputGroup
        disabled={!isScrapTypeNotListed}
        placeholder={t('report:materials.preconsumerScrap.dialog.fields.newTypeOfScrap.label')}
        value={newScrapType ?? ''}
        onChange={evt => onNewScrapTypeChange(evt.target.value)}
      />
    </div>
  )
}
